import type { ReactNode } from 'react';

/**
 * Represents the structure of a route definition in the routing system.
 */
export type RouteDefinition = {
    /**
     * The React element to render for this route.
     * Must be a loadable React Element created with createLazyComponent().
     */
    element: ReactNode;
    /** The path pattern for this route */
    path: string;
    /** Optional ID for the root element of this route */
    rootId?: string;
    /**
     * Optional condition function.
     * When this function returns falsy, the route definition is not created.
     */
    condition?: () => boolean;
};

/**
 * Creates a route definition for use in the routing system.
 * This function is a main primitive for defining routes in the application.
 *
 * @param {RouteDefinition} options - The options for creating the route definition
 * @returns {RouteDefinition} The created route definition
 *
 * @example
 * ```tsx
 * import { createRouteDefinition, createLazyComponent } from '@luna/router';
 *
 * const LazyHomeComponent = createLazyComponent(() => import('./Home'));
 *
 * const homeRouteDefinition = createRouteDefinition({
 *   path: '/home',
 *   element: <LazyHomeComponent />,
 *   rootId: 'home-root',
 *   condition: () => isFeatureEnabled('home')
 * });
 *
 * // Usage in router configuration
 * const routeDefinitions = [
 *   homeRouteDefinition,
 *   // ... other route definitions
 * ];
 * ```
 *
 * @remarks
 * - The `element` should be a lazy-loaded component to enable code splitting.
 * - The `condition` function can be used for feature flags or access control.
 * - Multiple route definitions can be combined to create the full routing configuration.
 * - This system allows for modular and conditionally-loaded routes.
 */
export function createRouteDefinition(options: RouteDefinition): RouteDefinition {
    return options;
}
